import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Phone, Mail, MapPin, Clock, ArrowRight, Check } from 'lucide-react'
import { SEO } from '@/components/SEO'
import { LocalizedLink } from '@/components/LocalizedLink'

const subjects = ['info', 'commercial', 'press', 'careers', 'other'] as const

export function ContactsPage() {
  const { t } = useTranslation('common')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitted(true)
  }

  return (
    <>
      <SEO
        title={t('nav.contacts')}
        type="website"
      />

      {/* Hero */}
      <section className="relative w-full h-[70vh] min-h-[500px] max-h-[800px] flex items-end overflow-hidden bg-[#2A0E0E]">
        <div className="absolute inset-0">
          <img
            src="/images/mondo_fiorucci_Mondo-Fiorucci_1440x612-hero-img_DESKTOP.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2A0E0E] via-[#2A0E0E]/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2A0E0E]/70 via-transparent to-transparent" />
        </div>
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04] z-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-20 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 pb-24 pt-40">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-10 bg-[#DF631E]" />
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#DF631E]">
                  {t('contacts.hero.badge')}
                </span>
              </div>
              <h1
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-5"
                style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
              >
                {t('contacts.hero.title')}
              </h1>
              <p className="text-base md:text-lg text-white/60 max-w-lg leading-relaxed">
                {t('contacts.hero.subtitle')}
              </p>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#FDF8F3] to-transparent z-20" />
      </section>

      {/* Main content: Info + Form */}
      <section className="bg-[#FDF8F3] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-16 lg:gap-20">

            {/* Left: Contact info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-2"
            >
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px w-8 bg-[#DF631E]" />
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#DF631E]">
                  {t('contacts.info.title')}
                </span>
              </div>

              <div className="space-y-8">
                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-[#4E2118]/10">
                    <Phone className="w-4 h-4 text-[#DF631E]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4E2118]/40 mb-1">
                      {t('contacts.info.phone')}
                    </p>
                    <a
                      href="tel:+3906911931"
                      className="text-[#4E2118] font-medium hover:text-[#DF631E] transition-colors"
                    >
                      {t('contact.phone')}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-[#4E2118]/10">
                    <Mail className="w-4 h-4 text-[#DF631E]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4E2118]/40 mb-1">
                      {t('contacts.info.email')}
                    </p>
                    <a
                      href="mailto:info@fioruccifood.it"
                      className="text-[#4E2118] font-medium hover:text-[#DF631E] transition-colors"
                    >
                      {t('contact.email')}
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-[#4E2118]/10">
                    <MapPin className="w-4 h-4 text-[#DF631E]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4E2118]/40 mb-1">
                      {t('contacts.info.address')}
                    </p>
                    <p className="text-[#4E2118] font-medium">
                      {t('contacts.info.companyName')}
                    </p>
                    <p className="text-[#4E2118]/60 text-sm">
                      {t('contact.address')}<br />
                      {t('contact.city')}
                    </p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-[#4E2118]/10">
                    <Clock className="w-4 h-4 text-[#DF631E]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#4E2118]/40 mb-1">
                      {t('contacts.info.hours')}
                    </p>
                    <p className="text-[#4E2118] font-medium">
                      {t('contacts.info.hoursValue')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Contact form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="lg:col-span-3"
            >
              <div className="flex items-center gap-4 mb-3">
                <span className="h-px w-8 bg-[#DF631E]" />
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#DF631E]">
                  {t('contacts.form.title')}
                </span>
              </div>
              <p className="text-sm text-[#4E2118]/50 mb-8">
                {t('contacts.form.subtitle')}
              </p>

              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-20 border border-[#4E2118]/8 bg-white"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-[#DF631E] mb-5">
                    <Check className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-[#4E2118] font-medium text-center max-w-sm">
                    {t('contacts.form.success')}
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#4E2118]/40 mb-2"
                      >
                        {t('contacts.form.name')}
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        required
                        className="w-full px-4 py-3 bg-white border border-[#4E2118]/10 text-[#4E2118] text-sm placeholder:text-[#4E2118]/30 focus:outline-none focus:border-[#DF631E] transition-colors"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#4E2118]/40 mb-2"
                      >
                        {t('contacts.form.email')}
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        className="w-full px-4 py-3 bg-white border border-[#4E2118]/10 text-[#4E2118] text-sm placeholder:text-[#4E2118]/30 focus:outline-none focus:border-[#DF631E] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label
                      htmlFor="contact-subject"
                      className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#4E2118]/40 mb-2"
                    >
                      {t('contacts.form.subject')}
                    </label>
                    <select
                      id="contact-subject"
                      required
                      defaultValue=""
                      className="w-full px-4 py-3 bg-white border border-[#4E2118]/10 text-[#4E2118] text-sm focus:outline-none focus:border-[#DF631E] transition-colors appearance-none"
                    >
                      <option value="" disabled>—</option>
                      {subjects.map((s) => (
                        <option key={s} value={s}>
                          {t(`contacts.form.subjects.${s}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="contact-message"
                      className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#4E2118]/40 mb-2"
                    >
                      {t('contacts.form.message')}
                    </label>
                    <textarea
                      id="contact-message"
                      rows={5}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#4E2118]/10 text-[#4E2118] text-sm placeholder:text-[#4E2118]/30 focus:outline-none focus:border-[#DF631E] transition-colors resize-none"
                    />
                  </div>

                  {/* Privacy */}
                  <div className="flex items-start gap-3">
                    <input
                      id="contact-privacy"
                      type="checkbox"
                      required
                      className="mt-1 w-4 h-4 border border-[#4E2118]/20 accent-[#DF631E]"
                    />
                    <label htmlFor="contact-privacy" className="text-sm text-[#4E2118]/60">
                      {t('contacts.form.privacy')}{' '}
                      <LocalizedLink to="privacy" className="text-[#DF631E] hover:underline">
                        {t('contacts.form.privacyLink')}
                      </LocalizedLink>
                    </label>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    className="group inline-flex items-center gap-3 px-7 py-3.5 bg-[#DF631E] text-white text-sm font-semibold tracking-wide uppercase hover:bg-[#B85018] transition-colors duration-300"
                  >
                    {t('contacts.form.send')}
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
